"use client";

import { type ReactNode, useCallback, useSyncExternalStore } from "react";
import { Braces, KeyRound } from "lucide-react";
import { QueryClientProvider } from "@tanstack/react-query";
import { DataChip } from "@/icons/DataTypeIcon";
import { VariableRefChip } from "@/components/SpotflowV2/Sidebar/VariableRefChip";
import { useBoundSourceHighlight } from "@/components/SpotflowV2/Sidebar/useBoundSourceHighlight";
import { BoundVariableTooltipContent } from "@/components/SpotflowV2/Sidebar/BoundVariableTooltip";
import type { VariableRefMeta } from "@/components/SpotflowV2/Sidebar/variableRefStatus";
import { isFileDataType } from "@/components/SpotflowV2/Sidebar/typeCompatibility";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/shadcn/tooltip";
import { queryClient } from "@/utils/queryClient";
import { getInputs, getStoreVersion, subscribeInputs } from "./inputsStore";
import { resolveChipDisplayStatus } from "./resolveChipDisplayStatus";

interface VariableChipProps {
  editorId: string;
  inputId: string;
}

const UNREACHABLE_WARNING =
  "The source node doesn't run before this one, so this value won't be available here.";
const INCOMPATIBLE_FILE_WARNING =
  "A file can't be inserted into text here: it would only appear as a placeholder. Bind it to a file field instead.";
const INCOMPATIBLE_WARNING =
  "This variable's type can't be inserted into text here.";

function incompatibleWarning(dataType: string | undefined): string {
  return isFileDataType(dataType)
    ? INCOMPATIBLE_FILE_WARNING
    : INCOMPATIBLE_WARNING;
}

function ChipTooltip({
  meta,
  variableRef,
  warning,
  highlight,
  unhighlight,
  children,
}: {
  meta: VariableRefMeta;
  variableRef: string;
  warning?: string;
  highlight: () => void;
  unhighlight: () => void;
  children: ReactNode;
}) {
  return (
    <QueryClientProvider client={queryClient}>
      <Tooltip delayDuration={400}>
        <TooltipTrigger asChild>
          <span
            className="inline-flex max-w-full"
            onMouseEnter={highlight}
            onMouseLeave={unhighlight}
          >
            {children}
          </span>
        </TooltipTrigger>
        <BoundVariableTooltipContent
          variableLabel={meta.variableLabel}
          dataType={meta.dataType}
          isList={meta.isList}
          sourceLabel={meta.sourceLabel}
          sourceImage={meta.sourceImage}
          sourceIconName={meta.sourceIconName}
          sourceColor={meta.sourceColor}
          variableRef={variableRef}
          isSecret={meta.isSecret}
          warning={warning}
        />
      </Tooltip>
    </QueryClientProvider>
  );
}

export function VariableChip({ editorId, inputId }: VariableChipProps) {
  const subscribe = useCallback(
    (listener: () => void) => subscribeInputs(editorId, listener),
    [editorId]
  );
  const getSnapshot = useCallback(() => getStoreVersion(editorId), [editorId]);

  useSyncExternalStore(subscribe, getSnapshot, () => 0);

  const { highlight, unhighlight } = useBoundSourceHighlight(inputId);

  const inputs = getInputs(editorId);
  const reachableInput = inputs.find((i) => i.id === inputId);
  const status = resolveChipDisplayStatus(inputId, reachableInput, editorId);
  const incompatible = reachableInput?.compatibility === "incompatible";

  const chip =
    status.kind === "valid" && reachableInput ? (
      <DataChip
        sourceImage={reachableInput.sourceImage}
        sourceIconName={reachableInput.sourceIconName}
        sourceColor={reachableInput.sourceColor}
        sourceLabel={reachableInput.sourceName ?? "Unbound"}
        dataType={reachableInput.dataType}
        variableLabel={reachableInput.name}
        isList={reachableInput.isList}
        warning={incompatible}
        size="sm"
        className="*:py-0"
        sourceIcon={
          reachableInput.sourceKind === "secret"
            ? KeyRound
            : reachableInput.sourceKind === "template"
              ? Braces
              : undefined
        }
      />
    ) : (
      <VariableRefChip status={status} size="sm" className="*:py-0" />
    );

  if (!status.meta) {
    const bareChip = (
      <span
        className="inline-flex max-w-full"
        onMouseEnter={highlight}
        onMouseLeave={unhighlight}
      >
        {chip}
      </span>
    );
    if (status.kind === "not_found") {
      return (
        <Tooltip delayDuration={300}>
          <TooltipTrigger asChild>{bareChip}</TooltipTrigger>
          <TooltipContent side="top" sideOffset={8} className="max-w-[260px]">
            This variable no longer exists: its source node may have been
            removed. Insert a new one with /.
          </TooltipContent>
        </Tooltip>
      );
    }
    return bareChip;
  }

  return (
    <ChipTooltip
      meta={status.meta}
      variableRef={inputId}
      warning={
        status.kind === "unreachable"
          ? UNREACHABLE_WARNING
          : incompatible
            ? incompatibleWarning(reachableInput?.dataType)
            : undefined
      }
      highlight={highlight}
      unhighlight={unhighlight}
    >
      {chip}
    </ChipTooltip>
  );
}
